// src/components/AiStaging.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const AiStaging = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [roomType, setRoomType] = useState('');
  const [prompts, setPrompts] = useState({});
  const [selectedStyle, setSelectedStyle] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [stagedImage, setStagedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (roomType) {
      fetchPrompts(roomType);
    }
  }, [roomType]);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchPrompts = async (type) => {
    try {
      const response = await fetch(`https://tophamtechbeb.com/api/stage/v1/${type}-prompts`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('API response for', type, ':', data);
        const promptKey = type === 'room' ? `${type}_staging_prompts` : `${type}_prompts`;
        const promptsArray = data[promptKey] || [];
        const mergedPrompts = {};
        promptsArray.forEach(promptObj => {
          Object.assign(mergedPrompts, promptObj);
        });
        console.log('Merged prompts:', mergedPrompts);
        setPrompts(mergedPrompts);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setTimeout(() => {
        document.querySelector('.staging-options-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      alert('Please select an image file');
    }
  };

  const handleStageRoom = async () => {
    let prompt;

    if (roomType === 'custom') {
      prompt = customPrompt;
    } else if (selectedStyle === 'random') {
      try {
        const response = await fetch(`https://tophamtechbeb.com/api/stage/v1/${roomType}-prompts/random`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        });
        if (response.ok) {
          const data = await response.json();
          prompt = data.prompt;
        } else {
          throw new Error('Failed to get random prompt');
        }
      } catch (error) {
        alert('Failed to get random prompt. Please try again.');
        return;
      }
    } else {
      prompt = prompts[selectedStyle];
    }

    if (!selectedFile || !prompt) {
      alert(roomType === 'custom' ? 'Please select an image and enter a custom prompt' : 'Please select an image, room type, and style');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('design_prompt', prompt);

      const response = await fetch('https://tophamtechbeb.com/api/stage/v1/stage-room', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' },
        body: formData
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setStagedImage(imageUrl);
        setTimeout(() => {
          document.querySelector('.staged-result-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        alert('Failed to stage room. Please try again.');
      }
    } catch (error) {
      console.error('Error staging room:', error);
      alert('Failed to stage room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = () => {
    if (stagedImage) {
      const link = document.createElement('a');
      link.href = stagedImage;
      link.download = 'staged-room.jpg';
      link.click();
    }
  };

  return (
    <div className="admin-dashboard-modern">
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">AI Room Staging Studio</h1>
            <p className="dashboard-subtitle">Transform property images with AI-powered staging</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin/digital-ads')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Digital Ads
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main-content">
        <div className="staging-container">
          <div className="staging-upload-section">
            <h3>Upload Image</h3>
            <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="file-input"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="upload-label">
                {selectedFile ? `📷 ${selectedFile.name}` : '📁 Choose Image File'}
              </label>
            </div>
            {previewUrl && (
              <div className="image-preview">
                <img src={previewUrl} alt="Preview" className="preview-image" />
              </div>
            )}
          </div>

          <div className="staging-options-section">
            <h3>Room Type</h3>
            <div className="room-type-buttons">
              {['room', 'bedroom', 'office', 'landscape', 'custom'].map(type => (
                <button
                  key={type}
                  className={`room-type-btn ${roomType === type ? 'active' : ''}`}
                  onClick={() => setRoomType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {roomType === 'custom' && (
              <div className="custom-prompt-section">
                <h4>Custom Prompt</h4>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your custom staging prompt (e.g., 'add modern furniture with blue accents, paint walls white, add contemporary art')..."
                  className="custom-prompt-textarea"
                  rows="3"
                />
              </div>
            )}

            {roomType && roomType !== 'custom' && Object.keys(prompts).length > 0 && (
              <div className="style-selection">
                <h3>Style</h3>
                <select
                  value={selectedStyle}
                  onChange={(e) => setSelectedStyle(e.target.value)}
                  className="style-dropdown"
                >
                  <option value="">Select a style</option>
                  <option value="random">Random</option>
                  {Object.keys(prompts).map(style => (
                    <option key={style} value={style}>
                      {style.charAt(0).toUpperCase() + style.slice(1)}
                    </option>
                  ))}
                </select>
                {selectedStyle && selectedStyle !== 'random' && (
                  <p className="style-description">{prompts[selectedStyle]}</p>
                )}
                {selectedStyle === 'random' && (
                  <p className="style-description">A random style will be selected when staging begins</p>
                )}
              </div>
            )}

            <button
              onClick={handleStageRoom}
              disabled={loading || !selectedFile || (roomType === 'custom' ? !customPrompt : !selectedStyle)}
              className="stage-button"
            >
              {loading ? 'Staging...' : '🤖 Stage Room'}
            </button>
          </div>

          {stagedImage && (
            <div className="staged-result-section">
              <h3>Staged Result</h3>
              <div className="staged-image-container">
                <img src={stagedImage} alt="Staged Room" className="staged-image" />
                <button onClick={downloadImage} className="download-button">
                  📥 Download Image
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AiStaging;